import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Typography,
  Box,
  Divider,
  Paper,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client/react';
import {
  MoneyTransactionFragmentFragment,
  Expense,
  CreateExpensePaymentDocument,
  CreateExpensePaymentMutation,
  CreateExpensePaymentMutationVariables,
} from '../../../generated/graphql';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../../components/app-context';
import { ExpenseSelectionDialog } from './expense-selection-dialog';
import { CreateExpenseFromTransactionDialog } from './create-expense-from-transaction-dialog';

interface ExpenseAssignmentDialogProps {
  open: boolean;
  transaction: MoneyTransactionFragmentFragment | null;
  onClose: (doRefresh: boolean) => void;
  onRefresh: () => void;
}

export function ExpenseAssignmentDialog({ open, transaction, onClose, onRefresh }: ExpenseAssignmentDialogProps) {
  const { show } = useAlert();
  const app = useApp();
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);
  const [amount, setAmount] = useState('');
  const [expenseSelectionOpen, setExpenseSelectionOpen] = useState(false);
  const [createExpenseOpen, setCreateExpenseOpen] = useState(false);

  const [createAssignment, { loading: creating }] = useMutation<
    CreateExpensePaymentMutation,
    CreateExpensePaymentMutationVariables
  >(CreateExpensePaymentDocument);

  // Calculate available unreconciled amount
  const availableAmount = transaction ? transaction.value - transaction.reconciledValue : 0;

  useEffect(() => {
    if (transaction) {
      // Set default amount to the full unreconciled value
      setAmount(availableAmount.toFixed(2));
      setSelectedExpense(null);
    }
  }, [transaction, availableAmount]);

  const handleExpenseSelect = (expense: Expense) => {
    setSelectedExpense(expense);
    setExpenseSelectionOpen(false);

    // Adjust amount if needed to not exceed the remaining expense amount
    const remaining = expense.amount - (expense.paidAmount || 0);
    const maxAmount = Math.min(availableAmount, remaining);
    setAmount(maxAmount.toFixed(2));
  };

  const handleSave = async () => {
    if (!transaction || !selectedExpense) {
      show.error('Vyberte výdavok.');
      return;
    }

    const amountValue = parseFloat(amount);
    if (!amountValue || amountValue <= 0) {
      show.error('Zadajte platnú sumu.');
      return;
    }

    if (amountValue > availableAmount) {
      show.error(
        'Suma nemôže presiahnuť nespárovanú hodnotu transakcie.',
        `${app.formatNumber(amountValue, 2)} > ${app.formatNumber(availableAmount, 2)}`,
      );
      return;
    }

    const remaining = selectedExpense.amount - (selectedExpense.paidAmount || 0);
    if (amountValue > remaining) {
      show.error(
        'Suma nemôže presiahnuť zostávajúcu hodnotu výdavku.',
        `${app.formatNumber(amountValue, 2)} > ${app.formatNumber(remaining, 2)}`,
      );
      return;
    }

    try {
      const result = await createAssignment({
        variables: {
          input: {
            transactionId: transaction.id,
            expenseId: selectedExpense.id,
            amount: amountValue,
          },
        },
      });

      if (result.data?.createExpensePayment?.errors?.length) {
        const error = result.data.createExpensePayment.errors[0];
        show.error('Chyba pri vytváraní priradenia.', error.code);
        return;
      }

      show.info('Platba bola úspešne priradená k výdavku.');
      onClose(true);
    } catch (error) {
      show.error('Nepodarilo sa priradiť platbu.', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleClose = () => {
    setSelectedExpense(null);
    setAmount('');
    onClose(false);
  };

  if (!transaction) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Priradenie platby k výdavku
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Transakcia
              </Typography>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">Dátum:</Typography>
                  <Typography variant="body2">{app.formatDate(transaction.transactionDate)}</Typography>
                </Stack>
                <Grid container spacing={1}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                      <Typography variant="body2">Suma:</Typography>
                      <Typography variant="body2">{app.formatNumber(transaction.value, 2)} €</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                      <Typography variant="body2">Už spárované:</Typography>
                      <Typography variant="body2">{app.formatNumber(transaction.reconciledValue, 2)} €</Typography>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                      <Typography variant="body2" fontWeight="bold">
                        K dispozícii:
                      </Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {app.formatNumber(availableAmount, 2)} €
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
                {transaction.reference && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2">Referencia:</Typography>
                    <Typography variant="body2">{transaction.reference}</Typography>
                  </Stack>
                )}
                {transaction.note && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ wordWrap: 'break-word' }}>
                      Poznámka: {transaction.note}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>

            <Divider />

            <Box>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Vybraný výdavok:</Typography>
                <Button variant="outlined" size="small" onClick={() => setExpenseSelectionOpen(true)}>
                  {selectedExpense ? 'Zmeniť výdavok' : 'Vybrať výdavok'}
                </Button>
                <Button variant="outlined" size="small" onClick={() => setCreateExpenseOpen(true)}>
                  Vytvoriť výdavok
                </Button>
              </Stack>

              {selectedExpense ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2">Názov:</Typography>
                      <Typography variant="body2">{selectedExpense.title}</Typography>
                    </Stack>
                    {selectedExpense.refNo && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2">Referencia:</Typography>
                        <Typography variant="body2">{selectedExpense.refNo}</Typography>
                      </Stack>
                    )}
                    <Grid container spacing={1}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                          <Typography variant="body2">Celková suma:</Typography>
                          <Typography variant="body2">{app.formatNumber(selectedExpense.amount, 2)} €</Typography>
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                          <Typography variant="body2">Už zaplatené:</Typography>
                          <Typography variant="body2">
                            {app.formatNumber(selectedExpense.paidAmount || 0, 2)} €
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <Stack direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between">
                          <Typography variant="body2" fontWeight="bold">
                            Zostáva zaplatiť:
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {app.formatNumber(selectedExpense.amount - (selectedExpense.paidAmount || 0), 2)} €
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nie je vybraný žiadny výdavok
                </Typography>
              )}
            </Box>

            <TextField
              label="Suma na priradenie"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              required
              inputProps={{ step: '0.01', min: '0', max: availableAmount.toString() }}
              helperText={`Maximálna suma: ${app.formatNumber(availableAmount, 2)} €`}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Zrušiť</Button>
          <Button onClick={handleSave} variant="contained" disabled={creating || !selectedExpense}>
            {creating ? 'Ukladám...' : 'Priradiť'}
          </Button>
        </DialogActions>
      </Dialog>

      <ExpenseSelectionDialog
        open={expenseSelectionOpen}
        onClose={() => setExpenseSelectionOpen(false)}
        onSelect={handleExpenseSelect}
        transaction={transaction}
      />

      <CreateExpenseFromTransactionDialog
        open={createExpenseOpen}
        transaction={transaction}
        onClose={() => setCreateExpenseOpen(false)}
        onCreated={() => {
          setCreateExpenseOpen(false);
          onClose(true);
        }}
      />
    </>
  );
}
